// Course levels and types
export const COURSE_LEVELS = [
  { value: 'A1', label: 'A1 - Beginner' },
  { value: 'A2', label: 'A2 - Elementary' },
  { value: 'B1', label: 'B1 - Intermediate' },
  { value: 'B2', label: 'B2 - Upper Intermediate' },
  { value: 'C1', label: 'C1 - Advanced' },
  { value: 'C2', label: 'C2 - Proficient' }
]

export const COURSE_MODES = [
  { value: 'online', label: 'Online Classes' },
  { value: 'offline', label: 'In-Person Classes' },
  { value: 'hybrid', label: 'Hybrid (Online + Offline)' }
]

export const COURSE_DURATIONS = [
  { value: '1-month', label: '1 Month' },
  { value: '2-months', label: '2 Months' },
  { value: '3-months', label: '3 Months' },
  { value: '6-months', label: '6 Months' }
]

// Course interface
export interface Course {
  id: string
  title: string
  level: string
  mode: string
  duration: string
  description: string
  price: number
  cultural_component: string
  is_active: boolean
}

// Sample course data
export const SAMPLE_COURSES: Course[] = [
  {
    id: '1',
    title: 'German for Beginners (A1)',
    level: 'A1',
    mode: 'hybrid',
    duration: '3-months',
    description: 'Start your German journey with basic vocabulary, grammar, and cultural insights.',
    price: 15000,
    cultural_component: 'Learn German through cultural immersion and tourism connections',
    is_active: true
  },
  {
    id: '2',
    title: 'Spanish for Beginners (A1)',
    level: 'A1',
    mode: 'hybrid',
    duration: '3-months',
    description: 'Begin your Spanish adventure with essential vocabulary and cultural understanding.',
    price: 15000,
    cultural_component: 'Explore Spanish culture through language, tourism, and traditions',
    is_active: true
  },
  {
    id: '3',
    title: 'French for Beginners (A1)',
    level: 'A1',
    mode: 'hybrid',
    duration: '3-months',
    description: 'Discover French language and culture with our comprehensive beginner course.',
    price: 15000,
    cultural_component: 'Experience French culture through language learning and cultural tourism',
    is_active: true
  },
  {
    id: '4',
    title: 'Intermediate German (B1)',
    level: 'B1',
    mode: 'online',
    duration: '2-months',
    description: 'Develop conversational skills and deeper understanding of German culture.',
    price: 18000,
    cultural_component: 'Advanced cultural immersion with tourism industry focus',
    is_active: true
  },
  {
    id: '5',
    title: 'Advanced German (C1)',
    level: 'C1',
    mode: 'offline',
    duration: '6-months',
    description: 'Master advanced German for professional and academic purposes.',
    price: 25000,
    cultural_component: 'Professional communication with European business culture',
    is_active: true
  }
]

// Export COURSES as alias for SAMPLE_COURSES for compatibility
export const COURSES = SAMPLE_COURSES

// Sample testimonials
export const SAMPLE_TESTIMONIALS = [
  {
    id: '1',
    student_name: 'Priya Sharma',
    course_completed: 'German A2',
    rating: 5,
    testimonial: 'The way they blend German learning with our Nepali culture made everything so relatable. I never thought learning German could be this enjoyable!',
    image_url: '/images/students/priya.jpg',
    is_featured: true
  },
  {
    id: '2',
    student_name: 'Rajesh Thapa',
    course_completed: 'German B1',
    rating: 5,
    testimonial: 'Excellent teaching methodology. The cultural connections helped me understand not just the language but also the German way of thinking.',
    image_url: '/images/students/rajesh.jpg',
    is_featured: true
  },
  {
    id: '3',
    student_name: 'Sita Gurung',
    course_completed: 'German A1',
    rating: 4,
    testimonial: 'Great experience! The teachers are very patient and the cultural immersion approach is unique. Highly recommended!',
    image_url: '/images/students/sita.jpg',
    is_featured: true
  }
]

// Cultural tourism packages
export const CULTURAL_PACKAGES = [
  {
    id: '1',
    title: 'German Language + Kathmandu Heritage',
    duration: '2 weeks',
    description: 'Learn German while exploring the rich heritage of Kathmandu Valley',
    highlights: [
      'Daily German classes (4 hours)',
      'Visit to Pashupatinath Temple',
      'Explore Boudhanath Stupa',
      'Traditional Newari cuisine experience',
      'Cultural workshops and interactions'
    ],
    image: '/images/packages/kathmandu-heritage.jpg'
  },
  {
    id: '2',
    title: 'German Language + Himalayan Adventure',
    duration: '3 weeks',
    description: 'Combine German learning with breathtaking Himalayan experiences',
    highlights: [
      'Intensive German course (6 hours daily)',
      'Everest Base Camp trek preparation',
      'Mountain village cultural immersion',
      'Traditional mountain cuisine',
      'Photography and nature workshops'
    ],
    image: '/images/packages/himalayan-adventure.jpg'
  }
]

// Navigation menu items
export const NAVIGATION_ITEMS = [
  { name: 'Home', href: '/' },
  { name: 'About Us', href: '/about' },
  { name: 'Courses', href: '/courses' },
  { name: 'Services', href: '/services' },
  { name: 'Blog', href: '/blog' },
  { name: 'Contact', href: '/contact' }
]

// Contact information
export const CONTACT_INFO = {
  address: 'Thamel, Kathmandu, Nepal',
  phone: '+977-1-4123456',
  email: 'info@nwlc.edu.np',
  hours: 'Sun-Fri: 9:00 AM - 6:00 PM',
  social: {
    facebook: 'https://facebook.com/nepalworldlanguagecenter',
    instagram: 'https://instagram.com/nwlc_nepal',
    linkedin: 'https://linkedin.com/company/nepal-world-language-center'
  }
}

// Cultural elements for design
export const CULTURAL_ELEMENTS = {
  colors: {
    primary: 'var(--color-primary)', // Dynamic Primary Color
    secondary: 'var(--color-secondary)', // Dynamic Secondary Color
    accent: 'var(--color-accent)', // Dynamic Accent Color
    warm: 'var(--color-warm)', // Dynamic Warm Color
    nature: 'var(--color-nature)' // Dynamic Nature Color
  },
  patterns: {
    mandala: '/images/cultural/mandala-pattern.svg',
    dhaka: '/images/cultural/dhaka-texture.png',
    lokta: '/images/cultural/lokta-paper.jpg'
  }
}

// Why choose us reasons with Lucide icon names
export const WHY_CHOOSE_US = [
  {
    title: 'European Expertise',
    description: 'Led by a motivated team who returned from Europe, bringing authentic global experience.',
    icon: 'Globe'
  },
  {
    title: 'Multiple Languages',
    description: 'Specialized courses in German, Spanish, and French to expand your global opportunities.',
    icon: 'Languages'
  },
  {
    title: 'Language, Culture, Tourism',
    description: 'Our unique motto integrates language learning with cultural immersion and tourism.',
    icon: 'Compass'
  },
  {
    title: 'Excellence Commitment',
    description: 'Dedicated to igniting passion for language learning and cross-cultural exchange.',
    icon: 'Award'
  }
]

// Cultural values with Lucide icon names
export const CULTURAL_VALUES = [
  {
    title: 'Respect',
    description: 'Honoring both German precision and Nepali hospitality',
    icon: 'Heart',
    color: 'primary'
  },
  {
    title: 'Growth',
    description: 'Nurturing language skills and cultural understanding',
    icon: 'Sprout',
    color: 'secondary'
  },
  {
    title: 'Unity',
    description: 'Building bridges between cultures and communities',
    icon: 'Users',
    color: 'accent'
  }
]

// Features with clean icons
export const FEATURES = [
  {
    title: 'Heritage Sites',
    description: 'Explore UNESCO World Heritage sites while practicing German',
    icon: 'Landmark'
  },
  {
    title: 'Local Cuisine',
    description: 'Learn German food vocabulary through Nepali culinary experiences',
    icon: 'ChefHat'
  },
  {
    title: 'Cultural Arts',
    description: 'Traditional music, dance, and art integrated with language learning',
    icon: 'Palette'
  },
  {
    title: 'Nature Immersion',
    description: 'Mountain treks and nature walks with German conversation practice',
    icon: 'Mountain'
  }
]